import * as livesessionNamespace from '@livesession/sdk';
import livesessionDefault from '@livesession/sdk';

type LiveSessionSdk = typeof import('@livesession/sdk').default;

function isLiveSessionApi(x: unknown): x is LiveSessionSdk {
  return (
    x != null &&
    typeof x === 'object' &&
    typeof (x as LiveSessionSdk).init === 'function' &&
    typeof (x as LiveSessionSdk).newPageView === 'function'
  );
}

/**
 * Walk `default` wrappers (CJS / Vite–Rolldown interop) until we find the real SDK object.
 */
function unwrapLiveSessionApi(root: unknown): LiveSessionSdk | undefined {
  const seen = new Set<unknown>();
  const stack: unknown[] = [root];

  while (stack.length > 0) {
    const x = stack.pop();
    if (x == null || seen.has(x)) continue;
    seen.add(x);

    if (isLiveSessionApi(x)) {
      return x;
    }

    if (typeof x === 'object' && 'default' in (x as object)) {
      stack.push((x as { default: unknown }).default);
    }
  }

  return undefined;
}

/**
 * Named export only — avoid `import ls from '…'` on this file, which can be wrapped
 * again as `{ default: … }` under Rolldown and break `ls.newPageView`.
 */
export const liveSessionSdk: LiveSessionSdk =
  unwrapLiveSessionApi(livesessionDefault) ??
  unwrapLiveSessionApi(livesessionNamespace) ??
  (() => {
    throw new Error(
      '[liveSessionSdk] Could not resolve @livesession/sdk (missing init/newPageView)',
    );
  })();
